// components/SupportSection.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, ChevronDown, ChevronUp, Clock, RefreshCw, CheckCircle, 
  XCircle, Paperclip, Send,
} from 'lucide-react';
import { format } from 'date-fns';
import AttachmentViewer from './AttachmentViewer'; 

const SupportSection = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [viewingAttachment, setViewingAttachment] = useState(null);

  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'order',
    message: '',
    attachments: []
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tickets', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }
      
      const data = await response.json();
      setTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch ticket details');
      }
      
      const data = await response.json();
      setSelectedTicket(data);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) {
      setFormError('Reply message cannot be empty');
      return;
    }
    
    try {
      // If there's an attachment, upload it first
      let attachmentUrl = null;
      if (attachment) {
        const formData = new FormData();
        formData.append('file', attachment);
        
        const uploadResponse = await fetch('/api/tickets/attachment', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload attachment');
        }
        
        const uploadResult = await uploadResponse.json();
        attachmentUrl = uploadResult.fileUrl;
      }
      
      // Now submit the reply
      const responseData = {
        message: replyText,
        ...(attachmentUrl ? { attachment: attachmentUrl } : {})
      };
      
      const response = await fetch(`/api/tickets/${selectedTicket._id}/responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(responseData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to send reply');
      }
      
      const data = await response.json();
      setSelectedTicket(data.ticket);
      setReplyText('');
      setAttachment(null);
      setFormError('');
      
      // Refresh the tickets list
      fetchTickets();
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newTicket.subject.trim()) {
      setFormError('Subject is required');
      return;
    }
    
    if (!newTicket.message.trim()) {
      setFormError('Message is required');
      return;
    }
    
    try {
      // Create ticket data
      const ticketData = {
        subject: newTicket.subject,
        message: newTicket.message,
        category: newTicket.category,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        attachments: newTicket.attachments
      };
      
      // Submit the ticket
      const response = await fetch('/api/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(ticketData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create ticket');
      }
      
      // Reset form and hide it
      setNewTicket({
        subject: '',
        category: 'order',
        message: '',
        attachments: []
      });
      setShowNewTicketForm(false);
      setFormError('');
      
      // Refresh tickets
      await fetchTickets();
      
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setFormError('File size must be less than 5MB');
        e.target.value = null;
        return;
      }
      
      setAttachment(file);
      setFormError('');
    }
  };

  const handleNewTicketAttachment = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setFormError('File size must be less than 5MB');
        e.target.value = null;
        return;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch('/api/tickets/attachment', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload file');
        }
        
        const result = await response.json();
        
        setNewTicket({
          ...newTicket,
          attachments: [...newTicket.attachments, result.fileUrl]
        });
        
        setFormError('');
        e.target.value = null;
      } catch (error) {
        setFormError(error.message);
      }
    }
  };

  const renderStatusBadge = (status) => {
    let bgColor, textColor, statusText;
    
    switch (status) {
      case 'open':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        statusText = 'Open';
        break;
      case 'in-progress':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        statusText = 'In Progress';
        break;
      case 'resolved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        statusText = 'Resolved';
        break;
      case 'closed':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        statusText = 'Closed';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        statusText = status;
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {statusText}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Support Tickets</h2>
        <button
          onClick={() => setShowNewTicketForm(!showNewTicketForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {showNewTicketForm ? 'Cancel' : 'New Ticket'}
        </button>
      </div>
      
      {formError && (
        <div className="bg-red-100 text-red-700 p-3 rounded-md">
          {formError}
        </div>
      )}
      
      {/* New Ticket Form */}
      {showNewTicketForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Create New Support Ticket</h3>
          
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Brief description of your issue"
              />
            </div>
            
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                value={newTicket.category}
                onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="order">Order Issue</option>
                <option value="product">Product Information</option>
                <option value="shipping">Shipping & Delivery</option>
                <option value="payment">Payment Issue</option>
                <option value="account">Account Help</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                rows="5"
                value={newTicket.message}
                onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Please describe your issue in detail"
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachments
              </label>
              
              <div className="space-y-2">
                {newTicket.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newTicket.attachments.map((url, index) => (
                      <div key={index} className="flex items-center bg-gray-100 px-3 py-1 rounded-md">
                        <Paperclip className="h-4 w-4 mr-1 text-gray-500" />
                        <span className="text-sm">Attachment {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => setNewTicket({
                            ...newTicket,
                            attachments: newTicket.attachments.filter((_, i) => i !== index)
                          })}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center">
                  <label className="block">
                    <span className="sr-only">Choose file</span>
                    <input 
                      type="file"
                      onChange={handleNewTicketAttachment}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100
                      "
                    />
                  </label>
                </div>
                
                <p className="text-xs text-gray-500">Max file size: 5MB. Allowed formats: images, PDF, documents</p>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Submit Ticket
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Tickets List */}
      <div>
        {loading ? (
          <div className="text-center py-10">
            <RefreshCw className="animate-spin h-8 w-8 mx-auto text-gray-400" />
            <p className="mt-2 text-gray-500">Loading tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white p-8 text-center rounded-lg shadow">
            <MessageSquare className="h-12 w-12 mx-auto text-gray-300" />
            <h3 className="mt-2 text-lg font-medium">No support tickets</h3>
            <p className="mt-1 text-gray-500">Create a new ticket to get help with your issue</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <div key={ticket._id} className="bg-white rounded-lg shadow overflow-hidden">
                <div 
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (expandedTicket === ticket._id) {
                      setExpandedTicket(null);
                    } else {
                      setExpandedTicket(ticket._id);
                      fetchTicketDetails(ticket._id);
                    }
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium">{ticket.subject}</h3>
                      {renderStatusBadge(ticket.status)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="inline-flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                      </span>
                      <span className="mx-2">•</span>
                      <span className="capitalize">{ticket.category}</span>
                    </div>
                  </div>
                  
                  {expandedTicket === ticket._id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                
                {expandedTicket === ticket._id && selectedTicket && (
                  <div className="p-4 border-t border-gray-200">
                    {/* Original message */}
                    <div className="mb-4">
                      <div className="text-sm text-gray-500 mb-1">
                        Your message on {format(new Date(selectedTicket.createdAt), 'MMM d, yyyy h:mm a')}:
                      </div>
                      <div className="bg-gray-50 p-3 rounded whitespace-pre-wrap">
                        {selectedTicket.message}
                      </div>
                      
                      {/* {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">Attachments:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {selectedTicket.attachments.map((url, index) => (                                
                              <a 
                                key={index}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center bg-gray-100 px-2 py-1 rounded text-sm hover:bg-gray-200"
                              >
                                <Paperclip className="h-4 w-4 mr-1" />
                                Attachment {index + 1}
                              </a>                          
                            ))}
                          </div>
                        </div>
                      )} */}
                      {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                        <div className="mt-2">
                            <p className="text-sm text-gray-500">Attachments:</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                            {selectedTicket.attachments.map((url, index) => {
                                // Get file extension to determine icon/thumbnail
                                const extension = url.split('.').pop().toLowerCase();
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
                                
                                return (
                                <button 
                                    key={index}
                                    onClick={() => setViewingAttachment(url)}
                                    className="flex items-center bg-gray-100 px-2 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                                >
                                    {isImage ? (
                                    <div className="w-5 h-5 mr-1 relative overflow-hidden bg-gray-200 rounded">
                                        <img 
                                        src={url} 
                                        alt="" 
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                        />
                                        <Paperclip className="h-4 w-4 absolute inset-0 m-auto hidden" />
                                    </div>
                                    ) : (
                                    <Paperclip className="h-4 w-4 mr-1" />
                                    )}
                                    Attachment {index + 1}
                                </button>
                                );
                            })}
                            </div>
                        </div>
                        )}

                    </div>
                    
                    {/* Responses */}
                    {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                      <div className="space-y-4 mb-4">
                        <h4 className="font-medium">Responses:</h4>
                        
                        {selectedTicket.responses.map((response, index) => (
                          <div 
                            key={index}
                            className={`p-3 rounded ${
                              response.createdBy === 'admin' 
                                ? 'bg-blue-50 ml-4' 
                                : 'bg-gray-50 mr-4'
                            }`}
                          >
                            <div className="flex justify-between text-sm text-gray-500 mb-1">
                              <span>{response.createdBy === 'admin' ? 'Support Agent' : 'You'}</span>
                              <span>{format(new Date(response.createdAt), 'MMM d, yyyy h:mm a')}</span>
                            </div>
                            <div className="whitespace-pre-wrap">{response.message}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply form */}
                    {selectedTicket.status !== 'closed' && (
                      <div className="mt-4">
                        <form onSubmit={handleSubmitReply}>
                          <div className="mb-3">
                            <label htmlFor="reply" className="block text-sm font-medium text-gray-700 mb-1">
                              Add Reply
                            </label>
                            <textarea
                              id="reply"
                              rows="3"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              placeholder="Type your reply here..."
                            ></textarea>
                          </div>
                          
                          <div className="flex items-center justify-between">
                             <label className="block">
                             {/* <span className="sr-only">Choose file</span>
                              <input 
                                type="file"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-gray-500
                                  file:mr-4 file:py-1.5 file:px-3
                                  file:rounded-md file:border-0
                                  file:text-sm file:font-medium
                                  file:bg-blue-50 file:text-blue-700
                                  hover:file:bg-blue-100
                                "
                              />*/}
                            </label> 
                            
                            <button
                              type="submit"
                              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                              <Send className="h-4 w-4 mr-1" /> Send
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {viewingAttachment && (
        <AttachmentViewer 
            url={viewingAttachment} 
            onClose={() => setViewingAttachment(null)} 
        />
       )}
    </div>
  );
};

export default SupportSection;